import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  fetchAccuracy, AccuracyNode, AccuracyCell, HORIZON_KEYS, pctLabel,
  primaryHitRate,
} from './accuracyApi';
import { PLACEHOLDER_ENABLED, placeholderNode } from './accuracyPlaceholder';
import { useI18n } from '../i18n/I18nContext';

/**
 * Colour scale.
 *
 * Replaces the previous rgba(255,255,255,0.35 / 0.20) alphas, which came out at
 * roughly 2.5:1 and 1.7:1 against the #0f2033 sidebar — below readable, and the
 * dimmest tier was carrying the H→N safety metric. These are explicit composites
 * with checked contrast ratios against that background:
 *
 *   value  #eef3f8   ~13:1   the numbers themselves
 *   body   #c3ced9   ~9:1    prose and secondary figures
 *   label  #93a3b5   ~5.6:1  uppercase labels, horizon captions
 *   faint  #7d8fa3   ~4.6:1  "—" placeholders only, never real data
 *
 * Status colours are unchanged — all three already clear 7:1 on this background.
 */
const C = {
  value:  '#eef3f8',
  body:   '#c3ced9',
  label:  '#93a3b5',
  faint:  '#7d8fa3',
  amber:  '#f0a500',
  red:    '#f87171',
  green:  '#4ade80',
  boxBg:      'rgba(255,255,255,0.03)',
  boxBorder:  'rgba(255,255,255,0.14)',
  chipBg:     'rgba(255,255,255,0.06)',
  chipBorder: 'rgba(255,255,255,0.14)',
};

function hitColour(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return C.faint;
  const pct = rate * 100;
  if (pct >= 90) return C.green;
  if (pct >= 70) return C.amber;
  return C.red;
}

/** HOCH read as NIEDRIG is the failure that misleads passengers — colour it. */
function missColour(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return C.faint;
  const pct = rate * 100;
  if (pct >= 10) return C.red;
  if (pct >= 5) return C.amber;
  return C.body;
}

const MONO = '"IBM Plex Mono"';

type Scope = 'overall' | 'line' | 'journey';

type Props = {
  day:   string;
  scope: Scope;
  line?:      string | null;
  journeyId?: string;
  /** Presentational mode: render this node directly, no fetch. */
  node?:  AccuracyNode | null;
  /** Overrides the heading above the box. */
  title?: string;
  /** Start collapsed. The header stays visible either way. */
  compact?: boolean;
  /**
   * Shown instead of "no data" when the caller knows WHY there is none.
   *
   * "No data" and "not held for this date" are different claims and the box
   * cannot tell them apart from an empty node: the first says the day had no
   * samples, the second says this source does not cover the day. Only the
   * caller knows which.
   */
  note?: string;
};

/**
 * Forecast-accuracy summary.
 *
 * Layout: a clickable header ABOVE the panel carrying the section label and an
 * up/down arrow, then the bordered panel itself, whose first line names the scope
 * (e.g. "LINIE S1"). The whole header is the hit target, not just the arrow —
 * a 10px chevron is too small to aim at.
 *
 * Collapsing hides the entire panel, not just the detail lines. There is one
 * toggle rather than two, so nothing about the metric can be permanently
 * unreachable from the UI.
 *
 * Evaluation is per UIC (see backend/accuracy.js).
 */
const AccuracyBox: React.FC<Props> = ({ day, scope, line, journeyId, node, title, compact, note }) => {
  const { t } = useI18n();
  const [fetched, setFetched] = useState<AccuracyNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [open,    setOpen]    = useState(!compact);
  // True only when a real fetch failed AND REACT_APP_ACCURACY_PLACEHOLDER=1.
  // Clears itself as soon as the endpoint responds, so it cannot outlive the
  // missing cloud function.
  const [placeholder, setPlaceholder] = useState<AccuracyNode | null>(null);

  const selfFetch = node === undefined;

  useEffect(() => {
    if (!selfFetch) return;
    if (scope === 'line' && !line) return;
    if (scope === 'journey' && !journeyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPlaceholder(null);
    fetchAccuracy({
      day,
      line:      scope === 'line'    ? (line ?? undefined) : undefined,
      journeyId: scope === 'journey' ? journeyId           : undefined,
      slim:      scope === 'overall',
    })
      .then(r => {
        if (cancelled) return;
        if (scope === 'overall')      setFetched(r.overall);
        else if (scope === 'line')    setFetched(r.by_line[line!] ?? {});
        else                          setFetched(r.by_journey[journeyId!] ?? {});
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.message);
        if (PLACEHOLDER_ENABLED) {
          const key = scope === 'overall' ? 'overall' : (line ?? journeyId ?? 'overall');
          setPlaceholder(placeholderNode(key));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selfFetch, day, scope, line, journeyId]);

  if (scope === 'line' && !line) return null;
  if (scope === 'journey' && !journeyId) return null;

  const isPlaceholder = !!placeholder;
  // A failed fetch shows as an error UNLESS placeholders are standing in for it.
  const showError = !!error && !isPlaceholder;
  const data = selfFetch ? (fetched ?? placeholder) : (node ?? null);
  const all: AccuracyCell | undefined = data?.all;
  const hasData = !!data && !!all && all.n > 0;

  const scopeLabel =
    scope === 'line' && line ? t('acc.lineLabel', { line })
    : scope === 'journey' && journeyId ? journeyId.slice(-6)
    : t('acc.allLines');

  return (
    <Box>
      {/* Header — above the panel, and the toggle for the whole panel */}
      <Box
        component="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        sx={{
          display: 'flex', alignItems: 'center', gap: '6px',
          width: '100%', textAlign: 'left',
          background: 'none', border: 'none', p: 0, mb: '6px',
          cursor: 'pointer', fontFamily: MONO,
          '&:hover .acc-arrow': { color: C.amber },
        }}
      >
        <Typography component="span" sx={{
          fontFamily: MONO, fontSize: '10.5px',
          letterSpacing: '0.1em', color: C.label,
          textTransform: 'uppercase', lineHeight: 1.3,
        }}>
          {title ?? t('acc.title')}
        </Typography>

        {isPlaceholder && (
          <Box component="span" title={`Endpoint unavailable: ${error}`} sx={{
            px: '4px', py: '1px',
            fontSize: '8.5px', letterSpacing: '0.08em',
            color: '#1a1200', backgroundColor: C.amber,
            borderRadius: '2px', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            {t('acc.placeholder')}
          </Box>
        )}

        <Box component="span" className="acc-arrow" sx={{
          ml: 'auto', flexShrink: 0,
          fontSize: '11px', lineHeight: 1, color: C.label,
        }}>
          {open ? '▲' : '▼'}
        </Box>
      </Box>

      {open && (
        <Box sx={{
          border: isPlaceholder ? `1px dashed ${C.amber}` : `1px solid ${C.boxBorder}`,
          backgroundColor: C.boxBg,
          borderRadius: '4px',
          px: '8px', py: '7px',
        }}>
          {/* Scope label inside the panel */}
          <Typography sx={{
            fontFamily: MONO, fontSize: '10px',
            letterSpacing: '0.1em', color: C.label,
            textTransform: 'uppercase', mb: '6px',
          }}>
            {scopeLabel}
          </Typography>

          {loading && <CircularProgress size={14} sx={{ color: C.amber }} />}
          {showError && (
            <Typography sx={{ fontSize: '10.5px', color: C.red, fontFamily: MONO }}>{error}</Typography>
          )}

          {!loading && !showError && data && !hasData && (
            <Typography sx={{ fontSize: '11px', color: C.body, fontFamily: MONO }}>
              {note ?? t('acc.noData')}
            </Typography>
          )}

          {!loading && !showError && hasData && (
            <>
              {/* N+1..N+5 chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mb: '7px' }}>
                {HORIZON_KEYS.map(h => {
                  const cell = data![h];
                  const { rate, isModel } = primaryHitRate(cell);
                  const colour = hitColour(rate);
                  return (
                    <Box key={h} title={cell
                      ? `${h}: ${isModel ? t('acc.modelClass') : t('acc.asSent')} · n=${cell.n.toLocaleString()}`
                      : `${h}: ${t('acc.noSample')}`}
                      sx={{
                        px: '5px', py: '3px', borderRadius: '3px',
                        backgroundColor: C.chipBg,
                        border: `1px solid ${C.chipBorder}`,
                        textAlign: 'center', minWidth: '38px',
                      }}>
                      <Typography sx={{
                        fontFamily: MONO, fontSize: '9px',
                        letterSpacing: '0.05em', color: C.label, lineHeight: 1.3,
                      }}>
                        {h}
                      </Typography>
                      <Typography sx={{
                        fontFamily: MONO, fontSize: '14px',
                        fontWeight: 600, lineHeight: 1.3,
                        color: cell ? colour : C.faint,
                      }}>
                        {rate != null ? `${(rate * 100).toFixed(0)}%` : '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Pooled summary — forecaster-delivered class when joined */}
              {(() => {
                const { rate, isModel } = primaryHitRate(all);
                const missRate = isModel
                  ? all!.model_high_to_low_miss_rate
                  : all!.high_to_low_miss_rate;
                const sampleN = isModel ? all!.n_model : all!.n;
                return (
                  <>
                    <Typography sx={{ fontFamily: MONO, fontSize: '12px', color: C.body, letterSpacing: '0.02em' }}>
                      {t('acc.all')}:{' '}
                      <Box component="span" sx={{ color: hitColour(rate), fontWeight: 600, fontSize: '13px' }}>
                        {pctLabel(rate)}
                      </Box>
                      <Box component="span" sx={{ color: C.label, fontSize: '10px' }}>
                        {' '}({isModel ? t('acc.modelClass') : t('acc.asSent')})
                      </Box>
                    </Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: C.label, mt: '1px' }}>
                      {t('acc.samples', { n: sampleN.toLocaleString() })}
                    </Typography>

                    <Box sx={{ mt: '5px' }}>
                      <Typography sx={{ fontFamily: MONO, fontSize: '10.5px', color: C.body, letterSpacing: '0.02em' }}>
                        {t('acc.hnError')}:{' '}
                        <Box component="span" sx={{ color: missColour(missRate), fontWeight: 600 }}>
                          {pctLabel(missRate)}
                        </Box>
                        <Box component="span" sx={{ color: C.label }}>
                          {' '}{t('acc.ofHigh', { n: all!.n_high_actual.toLocaleString() })}
                        </Box>
                      </Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: '10.5px', color: C.body, letterSpacing: '0.02em' }}>
                        {t('acc.mae')}:{' '}
                        <Box component="span" sx={{ color: C.value }}>
                          {all!.mae_persons != null
                            ? `${all!.mae_persons} ${t('acc.persons')}`
                            : '—'}
                        </Box>
                      </Typography>
                      {isModel && (
                        <Typography sx={{ fontFamily: MONO, fontSize: '10px', color: C.label, mt: '2px' }}>
                          {t('acc.asSent')}: {pctLabel(all!.class_hit_rate)}
                          {' · '}{t('acc.hnError')} {pctLabel(all!.high_to_low_miss_rate)}
                        </Typography>
                      )}
                    </Box>
                  </>
                );
              })()}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AccuracyBox;
